export type ScheduleType = 'simple' | 'interval' | 'cron';
export type JobRunStatus = 'pending' | 'success' | 'failed' | 'timeout' | 'canceled';
export type IntervalUnit = 'seconds' | 'minutes' | 'hours' | 'days';

export type SimpleScheduleConfig = {
  time: string;
  timezone: string;
};

export type IntervalScheduleConfig = {
  intervalMs: number;
};

export type CronScheduleConfig = {
  pattern: string;
  timezone: string;
};

export type Schedule = {
  id: string;
  name: string;
  jobType: string;
  scheduleType: ScheduleType;
  scheduleConfig: SimpleScheduleConfig | IntervalScheduleConfig | CronScheduleConfig;
  payload: Record<string, unknown>;
  enabled: boolean;
  pausedAt: string | null;
  lastRunAt: string | null;
  nextRunAt: string | null;
  consecutiveFailures: number;
  totalRuns: number;
  totalSuccesses: number;
  createdAt: string;
  updatedAt: string;
};

// The API returns the raw scheduledJobs row — there's no persisted `status` column,
// so status is derived client-side from enabled/pausedAt/consecutiveFailures.
export type DerivedScheduleStatus = 'disabled' | 'paused' | 'failing' | 'active';

export function deriveScheduleStatus(schedule: Schedule): DerivedScheduleStatus {
  if (!schedule.enabled) return 'disabled';
  if (schedule.pausedAt) return 'paused';
  if (schedule.consecutiveFailures > 0) return 'failing';
  return 'active';
}

export const derivedStatusVariants: Record<
  DerivedScheduleStatus,
  'secondary' | 'warning' | 'success' | 'destructive'
> = {
  disabled: 'secondary',
  paused: 'warning',
  failing: 'destructive',
  active: 'success',
};

export type ScheduleRun = {
  id: string;
  scheduledJobId: string;
  jobId: string | null;
  status: JobRunStatus;
  scheduledFor: string;
  startedAt: string | null;
  finishedAt: string | null;
  durationMs: number | null;
  errorMessage: string | null;
  createdAt: string;
};

export type JobDefinition = {
  jobType: string;
  maxAttempts: number | null;
  timeoutSeconds: number | null;
  description: string | null;
  payloadTemplate: Record<string, unknown> | null;
};

export const runStatusVariants: Record<
  JobRunStatus,
  'secondary' | 'warning' | 'success' | 'destructive' | 'outline'
> = {
  pending: 'secondary',
  success: 'success',
  failed: 'destructive',
  timeout: 'warning',
  canceled: 'outline',
};

export function formatDateTime(value?: string | null): string {
  if (!value) return '\u2014';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '\u2014';
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
}

export function formatRelative(value?: string | null): string {
  if (!value) return '\u2014';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '\u2014';
  const deltaMs = Date.now() - date.getTime();
  const deltaSeconds = Math.round(deltaMs / 1000);

  if (deltaSeconds < -60) {
    const deltaMinutes = Math.round(-deltaSeconds / 60);
    if (deltaMinutes < 60) return `in ${deltaMinutes}m`;
    const deltaHours = Math.round(deltaMinutes / 60);
    if (deltaHours < 24) return `in ${deltaHours}h`;
    const deltaDays = Math.round(deltaHours / 24);
    return `in ${deltaDays}d`;
  }

  if (deltaSeconds < 60) return deltaSeconds < 0 ? 'soon' : `${deltaSeconds}s ago`;
  const deltaMinutes = Math.round(deltaSeconds / 60);
  if (deltaMinutes < 60) return `${deltaMinutes}m ago`;
  const deltaHours = Math.round(deltaMinutes / 60);
  if (deltaHours < 24) return `${deltaHours}h ago`;
  const deltaDays = Math.round(deltaHours / 24);
  return `${deltaDays}d ago`;
}

export function formatDuration(ms: number | null): string {
  if (!ms) return '\u2014';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

export function parsePayloadSafe(json: string): Record<string, unknown> {
  try {
    return JSON.parse(json);
  } catch {
    return {};
  }
}

export function intervalSecondsFromUnit(value: number, unit: IntervalUnit): number {
  switch (unit) {
    case 'seconds':
      return value;
    case 'minutes':
      return value * 60;
    case 'hours':
      return value * 3600;
    case 'days':
      return value * 86400;
  }
}

export function bestIntervalUnit(totalSeconds: number): { value: number; unit: IntervalUnit } {
  if (totalSeconds % 86400 === 0) return { value: totalSeconds / 86400, unit: 'days' };
  if (totalSeconds % 3600 === 0) return { value: totalSeconds / 3600, unit: 'hours' };
  if (totalSeconds % 60 === 0) return { value: totalSeconds / 60, unit: 'minutes' };
  return { value: totalSeconds, unit: 'seconds' };
}

export type ScheduleUpdateData = Partial<{
  name: string;
  nextRunAt: Date;
  enabled: boolean;
  scheduleType: ScheduleType;
  scheduleConfig: SimpleScheduleConfig | IntervalScheduleConfig | CronScheduleConfig;
  payload: Record<string, unknown>;
}>;

export const scheduleTypeOptions = [
  { id: 'simple', label: 'Simple (run once)' },
  { id: 'interval', label: 'Interval (recurring)' },
  { id: 'cron', label: 'Cron (advanced pattern)' },
];

export const intervalUnitOptions = [
  { id: 'seconds', label: 'Seconds' },
  { id: 'minutes', label: 'Minutes' },
  { id: 'hours', label: 'Hours' },
  { id: 'days', label: 'Days' },
];

export const enabledOptions = [
  { id: 'yes', label: 'Yes' },
  { id: 'no', label: 'No' },
];
